import { useState, useEffect, useCallback } from 'react'
import { AlertCircle, Download, ListPlus, Loader2, RefreshCw, Search } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AddSelectedToPOModal } from '@/components/po/AddSelectedToPOModal'
import { CreatePOModal } from '@/components/po/CreatePOModal'
import { EditPOModal } from '@/components/po/EditPOModal'
import { ApprovalModal } from '@/components/po/ApprovalModal'
import { MarkOrderedModal } from '@/components/po/MarkOrderedModal'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { FilterRail } from '@/components/dashboard/FilterRail'
import { SavedViewChips } from '@/components/dashboard/SavedViewChips'
import { DashboardTable, DashboardColumnSettings } from '@/components/dashboard/DashboardTable'
import { DashboardSubTabs } from '@/components/dashboard/DashboardSubTabs'
import { SyncShopifyButton } from '@/components/dashboard/SyncShopifyButton'
import { useDashboard, signalKind } from '@/hooks/useDashboard'
import { useDashboardStore } from '@/store/dashboardStore'
import { useUIStore } from '@/store/uiStore'
import { fmtIsoDate } from '@/lib/dates'
import type { SortingState } from '@tanstack/react-table'
import type { DashboardRow, SignalKind } from '@/hooks/useDashboard'
import type { POLineItemPrefill } from '@/store/uiStore'

function TableSkeleton() {
  return (
    <div className="p-4 space-y-1">
      {Array.from({ length: 12 }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

// Legend order and wording, as the team reads the signals. 'unknown' rows
// have no stripe, so they have no legend item either.
const LEGEND: { kind: Exclude<SignalKind, 'unknown'>; label: string }[] = [
  { kind: 'order', label: 'Order now' },
  { kind: 'stop', label: 'Stop' },
  { kind: 'high_returns', label: 'High returns' },
  { kind: 'liquidate', label: 'Liquidate' },
  { kind: 'ok', label: 'OK' },
  { kind: 'no_action', label: 'No action' },
]

/** Signal legend above the table. Counts are over the rows the table is
 *  showing (already filtered), so each number matches what the eye can count. */
function SignalLegend({ rows, refreshing }: { rows: DashboardRow[]; refreshing: boolean }) {
  const counts = new Map<SignalKind, number>()
  for (const r of rows) {
    const k = signalKind(r.action_signal)
    counts.set(k, (counts.get(k) ?? 0) + 1)
  }
  return (
    <div className="dash-legend" data-testid="signal-legend" aria-label="Signal legend">
      {LEGEND.map(({ kind, label }) => {
        const n = counts.get(kind) ?? 0
        return (
          <span
            key={kind}
            className={`dash-legend-item${n === 0 ? ' is-zero' : ''}`}
            data-testid={`legend-${kind}`}
          >
            <span className={`dash-swatch sig-${kind}`} />
            {label} <span className="dash-legend-count">{n}</span>
          </span>
        )
      })}
      {refreshing && (
        <span className="dash-refreshing-note" data-testid="dashboard-refreshing">
          Updating with the latest sync…
        </span>
      )}
    </div>
  )
}

function formatCost(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function toPrefill(rows: DashboardRow[]): POLineItemPrefill[] {
  return rows.map((r) => ({
    variant_id: Number(r.variant_id ?? 0),
    product_id: Number(r.product_id ?? 0),
    product_title: r.product_title ?? '',
    variant_title: r.variant_title ?? '',
    sku: r.sku ?? '',
    barcode: r.barcode ?? '',
    vendor: r.vendor ?? '',
    unit_cost: r.unit_cost ?? undefined,
    recommended_qty: r.recommended_qty ?? undefined,
    on_hand: r.on_hand ?? undefined,
    incoming: r.incoming ?? undefined,
    action_signal: r.action_signal ?? undefined,
    return_rate: r.return_rate ?? undefined,
  }))
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return
  const cols = Object.keys(rows[0])
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s
  }
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function Dashboard() {
  const { filters, selectedRowIds, clearSelected, setFilters } = useDashboardStore()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const [sorting, setSorting] = useState<SortingState>([])
  const [searchValue, setSearchValue] = useState(filters.search)

  const { data: rows = [], rawRows, isLoading, isError, refetch, rawCount, refreshing, warming } =
    useDashboard(filters)

  // Debounce toolbar search → store
  useEffect(() => {
    const t = setTimeout(() => {
      if (filters.search !== searchValue) setFilters({ search: searchValue })
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  // Keep input in sync if store changes externally (e.g. clear / apply view)
  useEffect(() => {
    if (filters.search !== searchValue) setSearchValue(filters.search)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search])

  const selectedCount = selectedRowIds.size
  const selectedRows: DashboardRow[] = rows.filter((r) =>
    selectedRowIds.has(String(r.variant_id ?? ''))
  )
  const selectedCost = selectedRows.reduce(
    (s, r) => s + (r.unit_cost ?? 0) * (r.recommended_qty ?? 1),
    0
  )

  const openCreatePO = useCallback(() => {
    if (selectedCount === 0) {
      setActiveModal('create')
      return
    }
    setActiveModal('create', undefined, toPrefill(selectedRows))
    clearSelected()
  }, [selectedCount, selectedRows, setActiveModal, clearSelected])

  // Unlike openCreatePO this keeps the selection — the modal clears it via
  // onAdded once the items actually land on the PO.
  const openAddToPO = useCallback(() => {
    if (selectedCount === 0) return
    setActiveModal('addToPO', undefined, toPrefill(selectedRows))
  }, [selectedCount, selectedRows, setActiveModal])

  return (
    <div className="dash-page brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />

      <div className="flex flex-1 overflow-hidden min-h-0">
        <FilterRail rows={rawRows ?? []} sorting={sorting} />

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Saved-view chips + toolbar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderBottom: '1px solid var(--line-warm)',
              background: 'var(--surface-card)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ position: 'relative', flex: '0 0 280px' }}>
              <Search
                className="h-3 w-3"
                style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--mute)', pointerEvents: 'none' }}
              />
              <input
                type="text"
                data-testid="toolbar-search"
                placeholder="Search products by title, SKU, barcode…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{
                  width: '100%',
                  height: 28,
                  padding: '0 8px 0 26px',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  color: 'var(--text-primary)',
                  background: 'var(--surface-card)',
                  border: '1px solid var(--line-input)',
                  outline: 'none',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--brand)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line-input)' }}
              />
            </div>
            <SavedViewChips sorting={sorting} />
            <div style={{ flex: 1 }} />
            <DashboardSubTabs />
            <SyncShopifyButton />
            <DashboardColumnSettings />
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-[var(--ink-2)]"
              disabled={selectedCount === 0}
              title={selectedCount === 0 ? 'Select one or more rows to export' : `Export ${selectedCount} selected row(s)`}
              onClick={() => {
                if (!selectedRows.length) { toast.error('No rows selected to export'); return }
                const date = fmtIsoDate(new Date().toISOString())
                downloadCsv(`inventory-selected-${date}.csv`, selectedRows.map((r) => ({
                  'Product Title': r.product_title,
                  'Variant': r.variant_title,
                  'SKU': r.sku,
                  'Barcode': r.barcode,
                  'Vendor': r.vendor,
                  'Product Type': r.product_type,
                  'Tags': Array.isArray(r.tags) ? r.tags.join('; ') : r.tags,
                  'Action Signal': r.action_signal,
                  'On Hand': r.on_hand,
                  'Incoming': r.incoming,
                  'True Velocity': r.true_velocity,
                  'Predicted Demand': r.predicted_demand,
                  'Recommended Qty': r.recommended_qty,
                  'Days Coverage': r.days_coverage,
                  'Unit Cost': r.unit_cost,
                  'Status': r.status,
                })))
                toast.success(`Exported ${selectedRows.length} selected row(s)`)
              }}
            >
              <Download className="mr-1 h-3 w-3" />
              Export Selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-3 text-xs"
              disabled={selectedCount === 0}
              title={selectedCount === 0 ? 'Select one or more rows to add to an existing PO' : `Add ${selectedCount} selected to an existing PO`}
              onClick={openAddToPO}
              data-testid="add-to-po"
            >
              <ListPlus className="mr-1 h-3 w-3" />
              {selectedCount > 0 ? `Add to PO · ${selectedCount}` : 'Add to PO'}
            </Button>
            <Button
              size="sm"
              className={`h-7 px-3 text-xs${selectedCount > 0 ? ' font-semibold' : ''}`}
              onClick={openCreatePO}
              data-testid="po-from-selected"
            >
              {selectedCount > 0 ? `PO from selected · ${selectedCount}` : 'New PO'}
            </Button>
          </div>

          {/* Table area */}
          <div className="flex-1 overflow-hidden flex flex-col" style={{ borderTop: '1px solid var(--line-warm)' }}>
            {!isLoading && !isError && <SignalLegend rows={rows} refreshing={refreshing} />}
            {isLoading && <TableSkeleton />}

            {/* Cold start after a restart: the backend is still building and
                useDashboard retries on its own — neutral, not the error card. */}
            {warming && !isLoading && (
              <div className="flex justify-center py-12" data-testid="dashboard-warming">
                <div className="flex items-center gap-2 text-xs text-[var(--mute)]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Building dashboard data… this takes up to 3 minutes after a restart.
                </div>
              </div>
            )}

            {isError && !warming && !isLoading && (
              <div className="flex justify-center py-12">
                <Card className="w-80 rounded-none shadow-none border-[var(--line-warm)] bg-[var(--surface-card)]">
                  <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5">
                    <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
                    <p className="text-sm text-[var(--ink-2)] text-center">
                      Failed to load inventory
                    </p>
                    <p className="text-xs text-[var(--mute)] text-center">
                      Retry, and if it keeps failing check the server log
                    </p>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => refetch()}>
                      <RefreshCw className="mr-1.5 h-3 w-3" />
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {!isLoading && !isError && (
              <DashboardTable
                rows={rows}
                sorting={sorting}
                onSortingChange={setSorting}
                onOpenCreatePO={openCreatePO}
              />
            )}
          </div>

          {/* Bottom status bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '6px 16px',
              borderTop: '1px solid var(--line-warm)',
              background: 'var(--surface-card)',
              flexShrink: 0,
            }}
          >
            <span className="dash-countpill" data-testid="row-count">
              {rows.length} rows
              {selectedCount > 0 && ` · ${selectedCount} selected · ${formatCost(selectedCost)} cost`}
              {rawCount !== rows.length && ` (${rawCount} total)`}
            </span>
            <div style={{ flex: 1 }} />
            <span className="dash-micro">
              ↑↓ navigate · Space select · ⏎ open · C = Create PO
            </span>
          </div>
        </div>
      </div>

      {/* Modals — same set as PurchaseOrders */}
      <CreatePOModal />
      <AddSelectedToPOModal onAdded={clearSelected} />
      <EditPOModal />
      <ApprovalModal />
      <MarkOrderedModal />
    </div>
  )
}
